import * as Icons from "@ant-design/icons";
import { LinkOutlined } from "@ant-design/icons";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { PageLoading } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history, Link } from "@umijs/max";
import { Badge } from "antd";
import React, { useEffect } from "react";
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  FooterLinks,
  Question,
  SelectLang,
  ThemeSwitcher,
  RightHeader,
  MenuFooterTime,
} from "@/components";
import { getUserInfo, type SysMenuDataItem } from "@/services/login";
import { useReviewPendingCountStore } from "@/stores/useReviewPendingCountStore";
import { usePlatformRedDotStore } from "@/stores/usePlatformRedDotStore";
import { useUserStore } from "@/stores/useUserStore";
import { checkPermission } from "@/utils/permission";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";
import "@ant-design/v5-patch-for-react-19";
import "@/utils/time";

const isDev = process.env.NODE_ENV === "development";
const loginPath = "/login";
const REVIEW_CENTER_PATH = "/review_center";
const REVIEW_CENTER_PENDING_PATH = "/review_center/pending";
const MERCHANT_PATH = "/merchant";
const MERCHANT_APPLY_PATH = "/merchant/apply";
const MERCHANT_REVIEW_PATH = "/merchant/review";

/**
 * 菜单红点标记组件
 * 支持平台红点（operatorApplyPendingCount、reviewPendingCount）和商户红点（operatorApplyCount）
 */
const MenuBadge: React.FC<{
  label: React.ReactNode;
  path?: string;
}> = ({ label, path }) => {
  const operatorApplyPendingCount = usePlatformRedDotStore(
    (state) => state.operatorApplyPendingCount,
  );
  const reviewPendingCount = usePlatformRedDotStore(
    (state) => state.reviewPendingCount,
  );
  const operatorApplyCount = usePlatformRedDotStore(
    (state) => state.operatorApplyCount,
  );

  // 审核中心父级红点
  const showReviewCenterDot = reviewPendingCount > 0;
  // 商户管理父级红点
  const showMerchantDot =
    operatorApplyPendingCount > 0 || operatorApplyCount > 0;

  let displayCount: number | undefined;
  let showDot = false;

  if (path === REVIEW_CENTER_PATH) {
    showDot = showReviewCenterDot;
  } else if (path === REVIEW_CENTER_PENDING_PATH) {
    displayCount = reviewPendingCount;
  } else if (path === MERCHANT_PATH) {
    showDot = showMerchantDot;
  } else if (path === MERCHANT_APPLY_PATH) {
    displayCount = operatorApplyPendingCount;
  } else if (path === MERCHANT_REVIEW_PATH) {
    displayCount = operatorApplyCount;
  }

  const isParent = path === REVIEW_CENTER_PATH || path === MERCHANT_PATH;
  const hasContent =
    showDot || (displayCount !== undefined && displayCount > 0);

  if (!hasContent) {
    return <>{label}</>;
  }

  return (
    <>
      {label}
      <span
        style={{
          position: "absolute",
          right: isParent ? 36 : 16,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 10,
          lineHeight: 1,
        }}
      >
        {isParent ? (
          <Badge dot />
        ) : (
          <Badge count={displayCount} overflowCount={99} />
        )}
      </span>
    </>
  );
};

/**
 * 格式化菜单数据
 */
const formatMenus = (menus: SysMenuDataItem[]): any[] => {
  return menus
    .filter((item) => item.type === 1) // 仅渲染 type 为 1 的页面菜单
    .map((item) => {
      const IconComponent = (Icons as any)[item.icon];
      return {
        name: <MenuBadge label={item.name} path={item.address} />,
        path: item.address,
        icon: IconComponent ? React.createElement(IconComponent) : item.icon,
        children: item.children ? formatMenus(item.children) : undefined,
        key: item.id.toString(),
      };
    });
};

/**
 * 递归寻找第一个可用的叶子节点路径
 */
const findLeafPath = (menus: SysMenuDataItem[]): string | null => {
  for (const menu of menus) {
    if (menu.children && menu.children.length > 0) {
      const childPath = findLeafPath(menu.children);
      if (childPath) return childPath;
    }
    if (menu.type === 1 && menu.address && menu.address !== "/") {
      return menu.address;
    }
  }
  return null;
};

/**
 * 获取所有菜单路径
 */
const getAllMenuPaths = (menus: SysMenuDataItem[]): string[] => {
  let paths: string[] = [];
  menus.forEach((item) => {
    if (item.address) {
      paths.push(item.address);
    }
    if (item.children && item.children.length > 0) {
      paths = [...paths, ...getAllMenuPaths(item.children)];
    }
  });
  return paths;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any>;
}> {
  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        history.push(loginPath);
        return undefined;
      }
      const store = useUserStore.getState();
      // 刷新页面时，强制从接口获取最新用户信息
      const userInfo = await store.fetchUserInfo();
      return userInfo;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  const normalizedPathname =
    location.pathname.endsWith("/") && location.pathname !== "/"
      ? location.pathname.slice(0, -1)
      : location.pathname;

  if (![loginPath, "/main_login"].includes(normalizedPathname)) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

/**
 * 多 tab 认证同步组件
 * - 当其他 tab 登录新账号时（token 更新），当前 tab 刷新页面同步最新状态
 * - 当其他 tab 退出登录清除 token 时，当前 tab 同步退出
 */
const AuthSyncAcrossTabs: React.FC<{
  children: React.ReactNode;
  setInitialState: (fn: (state: any) => any) => void;
}> = ({ children, setInitialState }) => {
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "token") {
        if (!e.newValue) {
          // 其他 tab 移除了 token（退出登录）
          // 清除 zustand 用户状态
          useUserStore.getState().logout();
          // 清除 UmiJS initialState
          setInitialState((s: any) => ({ ...s, currentUser: undefined }));
          // 重定向到登录页
          history.push(loginPath);
        } else {
          // 其他 tab 登录了新账号（token 更新），刷新页面同步最新状态
          window.location.reload();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [setInitialState]);

  return <>{children}</>;
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  loading,
  setInitialState,
}) => {
  const refreshReviewPendingCount = () => {
    const currentUser = initialState?.currentUser;
    const { fetchPendingCount, resetPendingCount } =
      useReviewPendingCountStore.getState();

    if (
      !currentUser ||
      !checkPermission(currentUser.menus || [], REVIEW_CENTER_PENDING_PATH)
    ) {
      resetPendingCount();
      return;
    }

    void fetchPendingCount();
  };

  // 启动平台红点轮询
  const platFormRedDotStore = usePlatformRedDotStore.getState();
  platFormRedDotStore.startPolling();

  return {
    actionsRender: () => [],
    menuDataRender: () => {
      return formatMenus(initialState?.currentUser?.menus || []);
    },
    // avatarProps: {
    //   src: initialState?.currentUser?.avatar,
    //   title: <AvatarName />,
    //   render: (_, avatarChildren) => {
    //     return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
    //   },
    // },
    fixedHeader: false,
    footerRender: false,
    // pageHeaderRender: (props: any) => {
    //   return (
    //     <div
    //       style={{
    //         display: "flex",
    //         justifyContent: "space-between",
    //         alignItems: "center",
    //         padding: "8px 24px",
    //         background: "rgba(255, 255, 255, 0.4)",
    //         backdropFilter: "blur(8px)",
    //         borderBottom: "1px solid #f0f0f0",
    //       }}
    //     >
    //       <div>{props.breadcrumbRender?.(props)}</div>
    //       <FooterLinks />
    //     </div>
    //   );
    // },
    pageHeaderRender: null,
    waterMarkProps: {
      content: `${initialState?.currentUser?.id || ""}-${initialState?.currentUser?.userName || initialState?.currentUser?.nickName}`,
      font: {
        color: "rgba(0, 0, 0, 0.05)",
        fontSize: 2,
      },
    },
    onPageChange: () => {
      const { location } = history;
      // 规范化路径，移除末尾斜杠
      const normalizedPathname =
        location.pathname.endsWith("/") && location.pathname !== "/"
          ? location.pathname.slice(0, -1)
          : location.pathname;

      // 白名单路径，不需要登录即可访问
      const authWhiteList = [loginPath, "/main_login"];

      // 如果没有登录，且当前不在白名单中，重定向到 login
      if (
        !initialState?.currentUser &&
        !authWhiteList.includes(normalizedPathname)
      ) {
        history.push(loginPath);
        return;
      }

      // 路由权限拦截
      if (initialState?.currentUser) {
        // refreshReviewPendingCount();

        // 处理根路径重定向
        if (location.pathname === "/") {
          const firstPath = findLeafPath(initialState.currentUser.menus || []);
          if (firstPath) {
            history.replace(firstPath);
          } else {
            history.replace("/404");
          }
          return;
        }

        // 白名单路径
        const whiteList = [loginPath, "/404"];
        if (whiteList.includes(location.pathname)) return;

        // 获取当前用户的所有授权路径
        const menus = initialState.currentUser.menus || [];
        const menuPaths = getAllMenuPaths(menus);

        // 如果当前路径不在授权路径中
        const normalizedPathname = location.pathname.endsWith("/")
          ? location.pathname.slice(0, -1)
          : location.pathname;

        if (
          !menuPaths.some(
            (p) =>
              p === normalizedPathname ||
              (p.endsWith("/") ? p.slice(0, -1) : p) === normalizedPathname,
          )
        ) {
          history.push("/404");
          return;
        }

        // 商户点击 /merchant/review 时消除红点
        if (location.pathname === MERCHANT_REVIEW_PATH) {
          usePlatformRedDotStore.getState().dismissMerchantRedDot();
        }

        // 如果当前路径在授权路径中，但它是一个目录（有子菜单且其本身可能没有对应组件）
        // 我们尝试找到该目录下的第一个叶子节点并跳转
        const findMenuByPath = (
          menuList: SysMenuDataItem[],
          path: string,
        ): SysMenuDataItem | null => {
          for (const m of menuList) {
            if (m.address === path) return m;
            if (m.children) {
              const found = findMenuByPath(m.children, path);
              if (found) return found;
            }
          }
          return null;
        };

        const currentMenu = findMenuByPath(menus, location.pathname);
        if (
          currentMenu &&
          currentMenu.children &&
          currentMenu.children.length > 0
        ) {
          const leafPath = findLeafPath(currentMenu.children);
          if (leafPath) {
            history.push(leafPath);
          }
        }
      }
    },

    menuHeaderRender: (logo, title, other) => {
      const handleLogoClick = (e: React.MouseEvent) => {
        const currentPath = history.location.pathname;
        const menus = initialState?.currentUser?.menus || [];

        const firstMenuPath = findLeafPath(menus);

        const normalizedCurrentPath =
          currentPath.endsWith("/") && currentPath !== "/"
            ? currentPath.slice(0, -1)
            : currentPath;
        const normalizedFirstPath =
          firstMenuPath?.endsWith("/") && firstMenuPath !== "/"
            ? firstMenuPath?.slice(0, -1)
            : firstMenuPath;

        if (
          normalizedCurrentPath === normalizedFirstPath ||
          normalizedCurrentPath === "/"
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        if (firstMenuPath) {
          history.push(firstMenuPath);
        }
      };

      return (
        <div
          onClick={handleLogoClick}
          style={{
            width: "100%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
          }}
        >
          {!!other?.logo && (
            <img
              src={other?.logo as string}
              alt="logo"
              style={{ width: "30px", height: "30px", marginRight: "10px" }}
            />
          )}
          {!other?.collapsed && (
            <div style={{ fontWeight: 700 }}>Croco Manage</div>
          )}
        </div>
      );
    },
    menu: {
      locale: false,
    },
    menuFooterRender: (props) => {
      if (props?.collapsed) return null;
      return <MenuFooterTime collapsed={props?.collapsed} />;
    },
    title: "Croco Manage",
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      if (loading) return <PageLoading />;
      return (
        <AuthSyncAcrossTabs setInitialState={setInitialState}>
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <RightHeader></RightHeader>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                overscrollBehaviorY: "contain",
              }}
            >
              {children}
            </div>
          </div>
        </AuthSyncAcrossTabs>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: API_URL,
  ...errorConfig,
};
